import { DownOutlined, PlusOutlined } from '@ant-design/icons';
import AppButton from '@nxsys/components/AppButton';
import AppCard from '@nxsys/components/AppCard';
import AppCardTabListContainer from '@nxsys/components/AppCardTabListContainer';
import { AppConfirmationDelete } from '@nxsys/components/AppConfirmationDeleteModal';
import AppPageTitle from '@nxsys/components/AppPageTitle';
import AppsContainer from '@nxsys/components/AppsContainer';
import AppsContent from '@nxsys/components/AppsContainer/AppsContent';
import AppsHeader from '@nxsys/components/AppsContainer/AppsHeader';
import AppTableContainer from '@nxsys/components/AppTableContainer';
import { TimesheetStatusEnum } from '@nxsys/constants/AppEnums';
import {
  useApproveTimesheet,
  useCancelTimesheet,
  usePerformReversalPayrolls,
} from '@nxsys/hooks/api/timesheets';
import { useMobile } from '@nxsys/hooks/useMobile';
import { useNavigateWithQuery } from '@nxsys/hooks/useNavigateWithQuery';
import useNotificationSocket from '@nxsys/hooks/useNotificationSocket';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { TimesheetsState } from '@nxsys/store/slices/timesheets/reducer/timesheetsSlice';
import { GetListTimesheetsParams } from '@nxsys/store/slices/timesheets/type';
import { PerformReversalPayrollsParams } from '@nxsys/types/api';
import { TimeSheetDataItem } from '@nxsys/types/models/timesheet';
import { getTaxWeekTaxYear, showCurrencyByCountry } from '@nxsys/utils';
import { csvConfigExport } from '@nxsys/utils/export';
import { showMessage } from '@nxsys/utils/message';
import { formatMoney } from '@nxsys/utils/number-helper';
import { Dropdown } from 'antd';
import { ExportToCsv } from 'export-to-csv';
import { isEmpty, toNumber } from 'lodash';
import moment from 'moment';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useTheme } from 'styled-components';
import HeaderListing from '../components/HeaderListing';
import TimesheetReverseModal from '../components/TimesheetReverseModal';
import ChangeTaxPeriodModal from '../components/ChangeTaxPeriodModal';
import { useCanReversePayroll } from '@nxsys/hooks/useCanReversePayroll';
import { getColumns } from './column';
import { resolveChangeTaxPeriodSelection } from './changeTaxPeriodSelection';
import { IconArrowExport } from '@nxsys/components/AppIconSvg';
import { Icons } from '@nxsys/types/icons';

enum TIMESHEET_TAB {
  ALL = 'ALL',
  AWAITING_APPROVAL = 'AWAITING_APPROVAL',
  APPROVED = 'APPROVED',
  REJECTED = 'REJECTED',
}

interface ITimesheetsListingProps {
  timesheetsData: Pick<
    TimesheetsState['timesheetsData'],
    'list' | 'pagination' | 'queryParams'
  >;
  loading: boolean;
  getTimeSheetList: (params: GetListTimesheetsParams) => void;
  activeTab: TIMESHEET_TAB;
  setActiveTab: (tab: TIMESHEET_TAB) => void;
}

const TimesheetsListing: FC<ITimesheetsListingProps> = ({
  timesheetsData,
  loading,
  activeTab,
  setActiveTab,
  getTimeSheetList,
}) => {
  const { messages, formatMessage } = useTypedIntl();
  const navigate = useNavigateWithQuery();
  const theme = useTheme();
  const { isMobile } = useMobile();
  const location = useLocation();

  const { list, pagination, queryParams } = timesheetsData;
  const { payrollCountry, user, isBureau, isAgency, isEmployee } = useSelector(
    (state: AppState) => state.appStore,
  );
  const canReverse = useCanReversePayroll();

  const [selectedRows, setSelectedRows] = useState<TimeSheetDataItem[]>([]);

  const tabs = useMemo(
    () =>
      isEmployee
        ? [{ tab: messages['timesheet.all'], key: TIMESHEET_TAB.ALL }]
        : [
            { tab: messages['timesheet.all'], key: TIMESHEET_TAB.ALL },
            {
              tab: messages['timesheet.awaitingApproval'],
              key: TIMESHEET_TAB.AWAITING_APPROVAL,
            },
            {
              tab: messages['timesheet.approved'],
              key: TIMESHEET_TAB.APPROVED,
            },
            {
              tab: messages['timesheet.rejected'],
              key: TIMESHEET_TAB.REJECTED,
            },
          ],
    [],
  );

  const { run: cancelTimesheet, loading: isCancelTimesheetPending } =
    useCancelTimesheet();

  const { run: approveTimesheet } = useApproveTimesheet();

  const {
    run: performReversalPayrolls,
    loading: isPerformReversalPayrollsPending,
  } = usePerformReversalPayrolls();

  const [reverseModal, setReverseModal] = useState<{
    isOpen: boolean;
    payrollIds: string[];
  }>({
    isOpen: false,
    payrollIds: [],
  });

  const toggleReverseModal = () => {
    setReverseModal((prev) => ({
      ...prev,
      isOpen: !prev.isOpen,
    }));
  };

  const [taxPeriodModal, setTaxPeriodModal] = useState<{
    isOpen: boolean;
    timesheets: TimeSheetDataItem[];
  }>({
    isOpen: false,
    timesheets: [],
  });

  const toggleTaxPeriodModal = () => {
    setTaxPeriodModal((prev) => ({ ...prev, isOpen: !prev.isOpen }));
  };

  const handleTabChange = useCallback((key: TIMESHEET_TAB) => {
    setActiveTab(key);
    navigate(location.pathname, {
      query: {
        tab: key,
      },
    });
  }, []);

  const renderTitle = () => {
    return (
      <div className="flex items-center gap-x-3">
        <AppPageTitle
          prefix={messages['timesheet.allTimesheet']}
          separator=""
          title={''}
        />
      </div>
    );
  };

  const renderRightContentAgency = () => {
    return (
      <div className="flex items-center gap-x-2">
        <AppButton
          icon={<Icons.IconClose color={theme.palette.primary.main} />}
          variant="outlined"
          name={messages['timesheet.cancel']}
          onClick={handleCancelSelected}
        />
        <AppButton
          icon={<Icons.IconCheck color={theme.palette.primary.main} />}
          variant="outlined"
          name={messages['timesheet.approve']}
          onClick={handleApproveSelected}
        />
        <AppButton
          leftIcon={<IconArrowExport color={'#fff'} />}
          type="primary"
          name={messages['common.export']}
          onClick={handleExportSelected}
        />
      </div>
    );
  };

  const renderRightContent = () => {
    const menuProps = {
      items: [
        ...(activeTab === TIMESHEET_TAB.AWAITING_APPROVAL ||
        activeTab === TIMESHEET_TAB.ALL
          ? [
              {
                key: 'approve',
                label: messages['timesheet.approve'],
                onClick: () => handleApproveSelected(),
              },
            ]
          : []),
        ...(activeTab !== TIMESHEET_TAB.AWAITING_APPROVAL && canReverse
          ? [
              {
                key: 'reverseTimesheet',
                label: messages['timesheet.reverse'],
                onClick: () => handleReverseSelected(),
              },
            ]
          : []),
        {
          key: 'changeTaxPeriod',
          // NX-5237 (Victor 28/08, AC 6): don't soft-block the whole action when
          // a selected row is ineligible — pass every row through and let the BE
          // report the skipped ones in `failed`, like the other two tiles.
          label: messages['timesheet.changeTaxPeriod'],
          onClick: () => handleOpenChangeTaxPeriod(selectedRows),
        },
        {
          key: 'cancelTimesheet',
          label: messages['timesheet.cancel'],
          onClick: () => handleCancelSelected(),
        },
        {
          key: 'exportTimesheet',
          label: messages['common.export'],
          onClick: () => handleExportSelected(),
        },
      ],
    };

    return (
      <div className="flex items-center gap-x-2">
        <AppButton
          name={messages['common.addNew']}
          type="primary"
          onClick={() => navigate('/timesheets/add-new')}
          leftIcon={<PlusOutlined />}
        />
        <Dropdown menu={menuProps}>
          <AppButton
            type="primary"
            name={messages['common.action']}
            rightIcon={<DownOutlined />}
          />
        </Dropdown>
      </div>
    );
  };

  const handleApproveSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }

    const validItems = selectedRows.filter(
      (item) => item?.approveStatus === TimesheetStatusEnum.AWAITING_APPROVAL,
    );
    const ids = validItems.map((item) => item.id);

    if (ids.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['timesheet.noValidTimesheetToApprove'],
      });
      return;
    }
    approveTimesheet({ ids }).then(() => {
      showMessage({
        type: 'success',
        content: messages['timesheet.approveSuccess'],
      });
      refetchTimesheetList();
      setSelectedRows([]);
    });
  };

  // NX-5237: open the change-tax-period modal for one row (per-row icon) or the
  // current selection (bulk action). Only pre-payroll timesheets are eligible,
  // and a mixed pay-frequency selection is blocked.
  const handleOpenChangeTaxPeriod = (list: TimeSheetDataItem[]) => {
    const selection = resolveChangeTaxPeriodSelection(list);
    switch (selection.status) {
      case 'empty':
        showMessage({
          type: 'warning',
          content: messages['common.noSelected'],
        });
        return;
      case 'noneEligible':
        showMessage({
          type: 'warning',
          content: messages['timesheet.noValidTimesheetToChangeTaxPeriod'],
        });
        return;
      case 'mixedFrequency':
        showMessage({
          type: 'warning',
          content: messages['timesheet.changeTaxPeriodMixedFrequency'],
        });
        return;
      case 'ok':
        setTaxPeriodModal({ isOpen: true, timesheets: selection.selected });
        return;
    }
  };

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    if (tab) {
      setActiveTab(tab as TIMESHEET_TAB);
    }
  }, [location.search]);

  useEffect(() => {
    getTimeSheetList({
      page: 0,
      size: 20,
      taxYear: getTaxWeekTaxYear().taxYear,
    });
  }, [activeTab]);

  const refetchTimesheetList = () => {
    getTimeSheetList({ ...queryParams });
  };

  const onChangePagination = (page: number, pageSize: number) => {
    const isPageSizeChanged = pageSize !== pagination.pageSize;
    const shouldResetPage = isPageSizeChanged && pagination.current > 0;
    getTimeSheetList({
      ...queryParams,
      page: shouldResetPage ? 0 : page - 1,
      size: pageSize,
    });
  };

  const handleFilter = (params: GetListTimesheetsParams) => {
    getTimeSheetList({
      ...queryParams,
      ...params,
      page: 0,
    });
  };

  const handleExport = (data: TimeSheetDataItem[]) => {
    const dataExport = data.map((item) => {
      return {
        agencyRef: item?.subcontractorRef
          ? `${item?.subcontractorRef?.primaryAgency?.agencyPrefix || '___'}/${
              item?.subcontractorRef?.agencyRef || '___'
            }`
          : item?.starter
            ? item?.starter.agencyRef
            : '___',
        ...(isBureau
          ? {
              employeeId: item?.subcontractorRef?.id || '',
            }
          : {}),
        ...(!isBureau
          ? {
              assignmentName: item?.assignmentRef?.assignmentName || '',
            }
          : {}),
        status: item?.status || '',
        id: item?.id,
        ...(!isBureau
          ? {
              agencyName: item?.agencyRef?.name || '',
            }
          : {}),
        name: `${item?.subcontractorRef?.forename || ''} ${
          item?.subcontractorRef?.surname || ''
        }`,
        endDate: item?.endDate
          ? moment(item?.endDate).tz('Europe/London').format('DD/MM/YYYY')
          : '',
        batch: item?.batch || '',
        taxPeriod: item?.taxPeriod || '',
        taxYear: item?.taxYear || item?.currentYear || '',
        totalNet: formatMoney(
          toNumber(item?.totalNet) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        totalVat: formatMoney(
          toNumber(item?.totalVat) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        totalPrices: formatMoney(
          toNumber(item?.totalPrices) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        receipt: formatMoney(
          toNumber(item?.receipt) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        outstanding: formatMoney(
          toNumber(item?.outstanding) || 0,
          showCurrencyByCountry(payrollCountry),
        ),
        ...(!isBureau ? { invoiceNo: item?.invoiceNo || '' } : {}),
      };
    });

    const headers = [
      isBureau ? 'Employer Ref' : 'Agency Ref',
      ...(isBureau ? ['Employee ID'] : []),
      ...(!isBureau ? ['Assignment Name'] : []),
      'Status',
      'Timesheet ID',
      ...(!isBureau ? ['Agency Name'] : []),
      'Employee Name',
      'Week End Date',
      'Batch #',
      'Tax Period',
      'Tax Year',
      'Net',
      'VAT',
      'Total',
      'Receipt',
      'Outstanding',
      ...(!isBureau ? ['Sales INV Ref'] : []),
    ];

    const exportName = csvConfigExport(`Timesheet-export`, headers);
    const csvExporter = new ExportToCsv(exportName);
    csvExporter.generateCsv(dataExport);
  };

  const handleCancel = (list: TimeSheetDataItem[]) => {
    const validItems = list.filter(
      (item) =>
        item?.approveStatus !== TimesheetStatusEnum.CANCEL &&
        !item?.newDraftPayroll &&
        !item?.newPayroll,
    );
    const ids = validItems.map((item) => item.id);
    if (ids.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['timesheet.noValidTimesheetToCancel'],
      });
      return;
    }
    AppConfirmationDelete.open({
      title: messages['timesheet.cancel'],
      paragraph: formatMessage(
        { id: 'timesheet.cancelConfirm' },
        {
          timesheetList: (
            <span style={{ color: theme.palette.blue[500] }}>
              {ids.join(', ')}
            </span>
          ),
        },
      ),
      onConfirm: async () => {
        await cancelTimesheet(ids);
        showMessage({
          type: 'success',
          content: messages['timesheet.cancelSuccess'],
        });
        refetchTimesheetList();
      },
    });
  };

  const handleOpenReverse = (list: TimeSheetDataItem[]) => {
    const filteredList = list?.filter(
      (item) => !item.subcontractorRef?.isIssuedP45 || item.isPAL,
    );
    if (isEmpty(filteredList)) {
      showMessage({
        type: 'error',
        content: messages['timesheet.employeeAreLeaverPayrollCouldNotChange'],
      });
      return;
    }

    const tsDraftPayrolls = filteredList.filter(
      (ts) => !ts.newPayroll && ts.newDraftPayroll,
    );
    const tsPayrolls = filteredList.filter((ts) => ts.newPayroll);

    const draftPayrolls = tsDraftPayrolls.map((ts) => ts.newDraftPayroll);
    const payrolls = tsPayrolls.map((ts) => ts.newPayroll);

    let reversePayrollIds: string[] = [];

    if (!isEmpty(payrolls)) {
      reversePayrollIds = payrolls
        ?.map((p) => p?.id ?? '')
        .filter((id) => !!id);
    } else if (!isEmpty(draftPayrolls)) {
      handleReverse({
        draftPayrollIds: draftPayrolls
          .map((p) => p?.id ?? '')
          .filter((id) => !!id),
        payrollIds: [],
      });
      return;
    } else {
      const tsCancels = filteredList.filter(
        (ts) => !ts.newPayroll && !ts.newDraftPayroll,
      );
      if (!isEmpty(tsCancels)) {
        showMessage({
          type: 'error',
          content: messages['timesheet.timesheetAreNotProcessed'],
        });
        return;
      }
    }
    setReverseModal({
      isOpen: true,
      payrollIds: reversePayrollIds,
    });
  };

  const handleView = useCallback(
    (id: string) => {
      if (isAgency) {
        navigate(`/timesheets-agency/${id}`, {
          query: {
            from: location.pathname,
          },
        });
        return;
      }

      navigate(`/timesheets/${id}`, {
        query: {
          from: location.pathname,
        },
      });
    },
    [navigate],
  );

  const handleCancelSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleCancel(selectedRows);
  };

  const handleReverseSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleOpenReverse(selectedRows);
  };

  const handleExportSelected = () => {
    if (selectedRows.length === 0) {
      showMessage({
        type: 'warning',
        content: messages['common.noSelected'],
      });
      return;
    }
    handleExport(selectedRows);
  };

  useNotificationSocket({
    eventName: `reversal-payroll-from-timesheet-${user?.id}`,
    onSuccess: () => {
      showMessage({
        type: 'success',
        content: messages['timesheet.reverseSuccess'],
      });
      refetchTimesheetList();
    },
    onError: () => {
      showMessage({
        type: 'error',
        content: messages['timesheet.reverseFailed'],
      });
    },
  });

  const handleReverse = async (params: PerformReversalPayrollsParams) => {
    await performReversalPayrolls({
      ...params,
      isTimesheet: true,
    }).then(() => {
      showMessage({
        type: 'info',
        content: messages['timesheet.reverseSuccessPending'],
      });
    });
  };

  const onChangeTable = (sorter?: { field: string; order: 'desc' | 'asc' }) => {
    if (sorter?.order) {
      handleFilter({
        ...queryParams,
        page: 0,
        sortField: sorter.field || '',
        sortType: sorter.order,
      });
    } else {
      handleFilter({
        ...queryParams,
        page: 0,
        sortField: undefined,
        sortType: undefined,
      });
    }
  };

  const isFiltering =
    !!queryParams?.search ||
    !!queryParams?.startDate ||
    !!queryParams?.endDate ||
    !!queryParams?.taxPeriodFrom ||
    !!queryParams?.taxPeriodTo ||
    !!queryParams?.batchFrom ||
    !!queryParams?.batchTo ||
    !!queryParams?.timesheetId ||
    !!queryParams?.importId;

  const tableContent = (
    <>
      <AppsHeader key={isAgency ? undefined : activeTab}>
        {/* NX-5237 (Victor 28/08): the import filter is internal-only — the
            `GET /time-sheet/imports` endpoint is now @Roles-guarded, so an agency
            user would 403. Hide it for agencies. */}
        <HeaderListing
          showImportFilter={!isAgency && !isEmployee}
          onFilter={handleFilter}
        />
      </AppsHeader>
      <AppsContent>
        <AppTableContainer
          rowKey="id"
          data={list ?? []}
          loading={loading}
          page={pagination.current}
          total={pagination.totalDocs}
          pageSize={pagination.pageSize}
          scroll={{ x: 3000, y: 700 }}
          titleEmpty={messages['table.empty']}
          columns={getColumns({
            messages,
            payrollCountry,
            handleCancel,
            handleOpenReverse,
            handleView,
            theme,
            isMobile,
            isBureau,
            isAgency,
            hideReverse: !canReverse,
            // NX-5237 (Andy 04/09): the agency may re-date its own timesheets
            // too, so the per-row icon is no longer hidden for them. The BE
            // scopes the change to the caller's own agency.
            handleChangeTaxPeriod: handleOpenChangeTaxPeriod,
          })}
          onChangePagination={onChangePagination}
          onChangeTable={onChangeTable}
          rowSelection={{
            type: 'checkbox',
            // Controlled so the "whole import" action can auto-tick the batch.
            selectedRowKeys: selectedRows.map((row) => row.id),
            onChange: (_: React.Key[], selectedRows: any[]) => {
              setSelectedRows(selectedRows);
            },
          }}
          noDataType={
            isFiltering
              ? 'noDataFound'
              : isAgency
                ? 'noDataToShow'
                : 'noDataCreated'
          }
          addNewBtnOnClick={
            isAgency || activeTab !== TIMESHEET_TAB.ALL
              ? undefined
              : () => navigate('/timesheets/add-new')
          }
        />
      </AppsContent>
    </>
  );

  return (
    <AppsContainer
      fullView
      type="bottom"
      title={renderTitle()}
      rightContent={
        isAgency ? renderRightContentAgency() : renderRightContent()
      }
      loading={isCancelTimesheetPending || isPerformReversalPayrollsPending}
    >
      <AppCard>
        {isAgency ? (
          tableContent
        ) : (
          <AppCardTabListContainer
            tabList={tabs}
            activeTab={activeTab}
            handleTabChange={handleTabChange}
          >
            {tableContent}
          </AppCardTabListContainer>
        )}
      </AppCard>
      <TimesheetReverseModal
        isOpen={reverseModal.isOpen}
        toggle={toggleReverseModal}
        payrollIds={reverseModal.payrollIds}
        onPerform={handleReverse}
      />
      <ChangeTaxPeriodModal
        isOpen={taxPeriodModal.isOpen}
        toggle={toggleTaxPeriodModal}
        timesheets={taxPeriodModal.timesheets}
        onSuccess={() => {
          refetchTimesheetList();
          setSelectedRows([]);
        }}
      />
    </AppsContainer>
  );
};

export default React.memo(TimesheetsListing);
